import { Helmet } from 'react-helmet-async'

const SITE_NAME = 'Polytoria'
const SITE_URL = 'https://polytoria.com'
const DEFAULT_IMAGE = 'https://polytoria.com/og-image.png'
const TWITTER_HANDLE = '@polytoria'

/**
 * SEO component for managing meta tags
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - Open Graph type (website, article, game)
 * @param {Object} props.game - Game-specific data for rich snippets
 * @param {boolean} props.noindex - Whether to noindex the page
 */
export function SEO({
  title,
  description = 'Play free browser & downloadable games. One platform, zero limits. Arcade, puzzle, racing and more!',
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  game = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Browser & Downloadable Games`
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />

      {/* Game-specific meta tags */}
      {game && (
        <>
          <meta property="og:type" content="game" />
          <meta property="game:genre" content={game.genre} />
          {game.rating && <meta property="game:rating" content={game.rating} />}
        </>
      )}

      {/* Additional SEO */}
      <meta name="application-name" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
    </Helmet>
  )
}

/**
 * Structured data for games (JSON-LD)
 */
export function GameStructuredData({ game }) {
  if (!game) return null

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    'name': game.title,
    'description': game.shortDescription || game.description,
    'image': game.image,
    'url': `${SITE_URL}/game/${game.slug}`,
    'genre': game.genre,
    'gamePlatform': 'Web Browser',
    'applicationCategory': 'Game',
    'operatingSystem': 'Any',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock'
    },
    ...(game.rating && {
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': game.rating,
        'bestRating': '5',
        'worstRating': '1',
        'ratingCount': game.reviews?.replace(/[^0-9]/g, '') || '100'
      }
    }),
    'author': {
      '@type': 'Organization',
      'name': SITE_NAME,
      'url': SITE_URL
    },
    'publisher': {
      '@type': 'Organization',
      'name': SITE_NAME,
      'url': SITE_URL,
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_URL}/logo.png`
      }
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

/**
 * Structured data for the website (JSON-LD)
 */
export function WebsiteStructuredData() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': SITE_NAME,
    'url': SITE_URL,
    'description': 'Play free browser & downloadable games. One platform, zero limits.',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${SITE_URL}/games?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

/**
 * Breadcrumb structured data (JSON-LD)
 */
export function BreadcrumbStructuredData({ items }) {
  if (!items || items.length === 0) return null

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': `${SITE_URL}${item.url}`
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default SEO
